import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, ArrowLeft, FileText, Globe, AlertTriangle, Scale, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PrintButton from '@/components/PrintButton';

export default function TermsOfService() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </Link>
            <div className="flex items-center gap-4">
              <PrintButton documentTitle={isEnglish ? 'Even Rounds - Terms of Service' : 'Even Rounds - Käyttöehdot'} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => i18n.changeLanguage(isEnglish ? 'fi' : 'en')}
              >
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'FI' : 'EN'}
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" role="main" aria-labelledby="terms-title">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-10 w-10 text-primary" aria-hidden="true" />
            <h1 id="terms-title" className="text-4xl font-heading font-bold">
              {isEnglish ? 'Terms of Service' : 'Käyttöehdot'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {isEnglish ? 'Last updated: December 2025' : 'Viimeksi päivitetty: Joulukuu 2025'}
          </p>
        </div>

        <div className="prose prose-stone max-w-none" role="article">
          {isEnglish ? (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">1. Agreement to Terms</h2>
                <p>
                  By accessing or using Even Rounds (&quot;the Service&quot;), operated by Vera Value Oy 
                  (Business ID: 3323295-9), you agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use our Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">2. Description of Service</h2>
                <p>Even Rounds provides:</p>
                <ul>
                  <li>Yarn inventory management tools</li>
                  <li>Project tracking features</li>
                  <li>Digital crochet pattern marketplace</li>
                  <li>Community features including blog and marketplace</li>
                  <li>AI-powered yarn label scanning</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">3. User Accounts</h2>
                <ul>
                  <li>You must sign in using Google Authentication</li>
                  <li>You are responsible for maintaining the security of your account</li>
                  <li>You must provide accurate information</li>
                  <li>You must be at least 16 years old to use the Service</li>
                  <li>One person may not maintain multiple accounts</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">4. User Content</h2>
                <p>
                  You retain ownership of content you create (yarn entries, projects, patterns). 
                  By posting content, you grant us a non-exclusive license to display and store 
                  that content for providing the Service.
                </p>
                <p>You agree not to post content that:</p>
                <ul>
                  <li>Infringes intellectual property rights</li>
                  <li>Is illegal, harmful, or offensive</li>
                  <li>Contains malware or spam</li>
                  <li>Impersonates others</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <CreditCard className="h-6 w-6 text-primary" />
                  5. Purchases & Payments
                </h2>
                <p>
                  Digital design purchases are processed securely through Stripe. All prices are 
                  displayed in EUR and include applicable VAT. By making a purchase, you agree to 
                  pay the stated price.
                </p>
                <p>
                  Voluntary donations/support payments are non-refundable gifts to help maintain 
                  the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">6. Intellectual Property</h2>
                <p>
                  The Even Rounds name, logo, and platform design are owned by Vera Value Oy. 
                  Digital patterns sold through our shop are licensed for personal use only unless 
                  otherwise stated.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <AlertTriangle className="h-6 w-6 text-primary" />
                  7. Disclaimers & Limitations
                </h2>
                <p>
                  The Service is provided &quot;as is&quot; without warranties of any kind. We do not guarantee 
                  that the Service will be uninterrupted or error-free. Our liability is limited to 
                  the maximum extent permitted by Finnish law.
                </p>
                <p>
                  AI-powered features (yarn scanning) provide suggestions and may not be 100% accurate. 
                  Always verify important information manually.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">8. Termination</h2>
                <p>
                  We may suspend or terminate your account for violations of these terms. You may 
                  delete your account at any time by contacting us. Upon termination, your right 
                  to use the Service ceases immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Scale className="h-6 w-6 text-primary" />
                  9. Governing Law & Dispute Resolution
                </h2>
                <p>
                  These terms are governed by the laws of Finland. Any disputes shall be resolved 
                  in the courts of Finland.
                </p>
                <p className="mt-4">
                  <strong>Consumer Dispute Resolution:</strong> For consumer disputes, you may contact the 
                  Finnish Consumer Disputes Board (Kuluttajariitalautakunta) before taking the matter to court. 
                  The board provides free-of-charge recommendations for resolving disputes between consumers and businesses.
                </p>
                <p className="mt-2">
                  <strong>Kuluttajariitalautakunta</strong><br />
                  Website: <a href="https://www.kuluttajariita.fi" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">kuluttajariita.fi</a>
                </p>
                <p className="mt-2">
                  You can also use the EU Online Dispute Resolution platform:<br />
                  <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://ec.europa.eu/odr</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">10. Changes to Terms</h2>
                <p>
                  We may update these terms from time to time. Continued use of the Service after 
                  changes constitutes acceptance of the new terms. We will notify users of 
                  significant changes via email or platform notification.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">11. Contact</h2>
                <p>
                  For questions about these terms:<br />
                  Vera Value Oy<br />
                  Email: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          ) : (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">1. Ehtojen hyväksyminen</h2>
                <p>
                  Käyttämällä Even Rounds -palvelua (&quot;Palvelu&quot;), jota ylläpitää Vera Value Oy 
                  (Y-tunnus: 3323295-9), sitoudut noudattamaan näitä käyttöehtoja. Jos et hyväksy 
                  näitä ehtoja, älä käytä Palveluamme.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">2. Palvelun kuvaus</h2>
                <p>Even Rounds tarjoaa:</p>
                <ul>
                  <li>Lankavaraston hallintatyökaluja</li>
                  <li>Projektien seurantaominaisuuksia</li>
                  <li>Digitaalisten virkkausohjeiden kauppapaikan</li>
                  <li>Yhteisöominaisuuksia, mukaan lukien blogi ja tori</li>
                  <li>Tekoälypohjaista lankaetikettien skannausta</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">3. Käyttäjätilit</h2>
                <ul>
                  <li>Sinun on kirjauduttava sisään Google-tunnistautumisella</li>
                  <li>Olet vastuussa tilisi turvallisuuden ylläpidosta</li>
                  <li>Sinun on annettava oikeat tiedot</li>
                  <li>Sinun on oltava vähintään 16-vuotias käyttääksesi Palvelua</li>
                  <li>Yhdellä henkilöllä ei saa olla useita tilejä</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">4. Käyttäjäsisältö</h2>
                <p>
                  Säilytät omistusoikeuden luomaasi sisältöön (lankamerkinnät, projektit, ohjeet). 
                  Julkaisemalla sisältöä myönnät meille ei-yksinomaisen lisenssin näyttää ja tallentaa 
                  kyseinen sisältö Palvelun tarjoamiseksi.
                </p>
                <p>Sitoudut olemaan julkaisematta sisältöä, joka:</p>
                <ul>
                  <li>Loukkaa immateriaalioikeuksia</li>
                  <li>On laitonta, haitallista tai loukkaavaa</li>
                  <li>Sisältää haittaohjelmia tai roskapostia</li>
                  <li>Esiintyy muina henkilöinä</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <CreditCard className="h-6 w-6 text-primary" />
                  5. Ostot ja maksut
                </h2>
                <p>
                  Digitaalisten mallien ostot käsitellään turvallisesti Stripen kautta. Kaikki hinnat 
                  näytetään euroina ja sisältävät sovellettavan ALV:n. Tekemällä ostoksen sitoudut 
                  maksamaan ilmoitetun hinnan.
                </p>
                <p>
                  Vapaaehtoiset lahjoitukset/tukimaksut ovat ei-palautettavia lahjoja Palvelun 
                  ylläpidon tukemiseksi.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">6. Immateriaalioikeudet</h2>
                <p>
                  Even Rounds -nimi, logo ja alustan design ovat Vera Value Oy:n omaisuutta. 
                  Kaupassamme myytävät digitaaliset ohjeet on lisensoitu vain henkilökohtaiseen 
                  käyttöön, ellei toisin mainita.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <AlertTriangle className="h-6 w-6 text-primary" />
                  7. Vastuunrajoitukset
                </h2>
                <p>
                  Palvelu tarjotaan &quot;sellaisenaan&quot; ilman minkäänlaisia takuita. Emme takaa, että 
                  Palvelu toimii keskeytyksettä tai virheettömästi. Vastuumme on rajoitettu Suomen 
                  lain sallimaan enimmäismäärään.
                </p>
                <p>
                  Tekoälypohjaiset ominaisuudet (lankaskannaus) tarjoavat ehdotuksia eivätkä 
                  välttämättä ole 100% tarkkoja. Tarkista tärkeät tiedot aina manuaalisesti.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">8. Sopimuksen päättäminen</h2>
                <p>
                  Voimme keskeyttää tai lopettaa tilisi näiden ehtojen rikkomisen vuoksi. Voit 
                  poistaa tilisi milloin tahansa ottamalla meihin yhteyttä. Päättymisen jälkeen 
                  oikeutesi käyttää Palvelua lakkaa välittömästi.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Scale className="h-6 w-6 text-primary" />
                  9. Sovellettava laki ja riitojen ratkaisu
                </h2>
                <p>
                  Näihin ehtoihin sovelletaan Suomen lakia. Mahdolliset riidat ratkaistaan Suomen 
                  tuomioistuimissa.
                </p>
                <p className="mt-4">
                  <strong>Kuluttajariitojen ratkaisu:</strong> Kuluttajariitojen osalta voit ottaa yhteyttä 
                  Kuluttajariitalautakuntaan ennen asian viemistä tuomioistuimeen. Lautakunta antaa 
                  maksuttomia suosituksia kuluttajien ja yritysten välisten riitojen ratkaisemiseksi.
                </p>
                <p className="mt-2">
                  <strong>Kuluttajariitalautakunta</strong><br />
                  Verkkosivusto: <a href="https://www.kuluttajariita.fi" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">kuluttajariita.fi</a>
                </p>
                <p className="mt-2">
                  Voit myös käyttää EU:n verkkovälitteistä riidanratkaisufoorumia:<br />
                  <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://ec.europa.eu/odr</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">10. Ehtojen muutokset</h2>
                <p>
                  Voimme päivittää näitä ehtoja ajoittain. Palvelun jatkuva käyttö muutosten 
                  jälkeen merkitsee uusien ehtojen hyväksymistä. Ilmoitamme merkittävistä 
                  muutoksista käyttäjille sähköpostitse tai alustan kautta.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">11. Yhteystiedot</h2>
                <p>
                  Näitä ehtoja koskevissa kysymyksissä:<br />
                  Vera Value Oy<br />
                  Sähköposti: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Vera Value Oy. {isEnglish ? 'All rights reserved.' : 'Kaikki oikeudet pidätetään.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
